import { type PayloadAction } from "@reduxjs/toolkit"
import { GroupBy } from "connected-components/analytics/types"
import type { ReduxState } from "model/state"
import { createPanelSlice, type PanelRootState } from "./create-panel-slice"
import { makeTemplateChartReducers, type TemplateChartSettings } from "./template-chart-settings"

export interface FactorsViewState {
  view: string
  // Opaque taxonomy object ({ id, label }), persisted as-is — not a bare id string. The analytics
  // hook reads groupBy.id / groupBy.label directly without widening to GroupBy | string | null.
  groupBy: GroupBy | null
  showMetricsSelector: boolean
  showNotHeld: boolean | null
  selectedPeriodPerformance: string
  sortMetricId: string | null
  sortDirection: "ASC" | "DESC"
  selectedTableLevels: string[]
  datagridState: Record<string, unknown>
  // Per-template chart toggles (content-set, performance), keyed by template name; defaults live
  // at the consumer call site.
  templateCharts: Partial<Record<string, TemplateChartSettings>>
  isPreferencesLoaded: boolean
  /** Transient (never persisted): set by clonePanelState so the duplicate's sync hook persists the
   *  in-memory clone once. Distinguishes a real clone from an ordinary remount of an already-loaded
   *  panel, which must not re-save. Cleared after the one-time save. */
  pendingClonePersist?: boolean
}

/** Keyed by dashboardId at the top, panelId at the inner level. */
export type FactorsViewRootState = PanelRootState<FactorsViewState>

/** Subtype for the factors-view preferences endpoint, and the panel-id fallback
 *  for instances rendered outside a PanelIdProvider. One string, one purpose. */
export const FACTORS_VIEW_PREFERENCE_BASE = "factors-view"

/** Persisted fields — single source for the setPreferences whitelist (here) and the sync hook's diff
 *  baseline. showMetricsSelector is a transient UI flag, intentionally absent so it can't persist. */
export const FACTORS_VIEW_PREFERENCE_FIELDS = [
  "view",
  "groupBy",
  "showNotHeld",
  "selectedPeriodPerformance",
  "sortMetricId",
  "sortDirection",
  "selectedTableLevels",
  "datagridState",
  "templateCharts",
] as const

/** Co-located access to a panel's raw slot (undefined when absent); callers coalesce to DEFAULT_FACTORS_VIEW_STATE. */
export const selectFactorsViewPanel = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): FactorsViewState | undefined => state.dashboard.factorsView[dashboardId]?.[panelKey]

export const DEFAULT_FACTORS_VIEW_STATE: FactorsViewState = {
  view: "risk",
  groupBy: null,
  showMetricsSelector: false,
  showNotHeld: null,
  selectedPeriodPerformance: "1M",
  sortMetricId: "riskDecomposition",
  sortDirection: "DESC",
  selectedTableLevels: [],
  datagridState: {},
  templateCharts: {},
  isPreferencesLoaded: false,
}

export const factorsViewSlice = createPanelSlice({
  name: "dashboard/factorsView",
  defaultState: DEFAULT_FACTORS_VIEW_STATE,
  preferenceFields: FACTORS_VIEW_PREFERENCE_FIELDS,
  freshOverrides: () => ({ selectedTableLevels: [], datagridState: {}, templateCharts: {} }),
  cloneOverrides: (sourceState) => ({
    selectedTableLevels: [...sourceState.selectedTableLevels],
    datagridState: { ...sourceState.datagridState },
    templateCharts: { ...sourceState.templateCharts },
  }),
  reducers: ({ ensure, initInstance }) => ({
    ...makeTemplateChartReducers<FactorsViewState>(ensure),
    setView(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, view: string }>,
    ) {
      const { dashboardId, panelId, view } = action.payload
      ensure(state, dashboardId, panelId).view = view
    },

    setGroupBy(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, groupBy: GroupBy | null }>,
    ) {
      const { dashboardId, panelId, groupBy } = action.payload
      ensure(state, dashboardId, panelId).groupBy = groupBy
    },

    setShowNotHeld(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, showNotHeld: boolean | null }>,
    ) {
      const { dashboardId, panelId, showNotHeld } = action.payload
      ensure(state, dashboardId, panelId).showNotHeld = showNotHeld
    },

    setShowMetricsSelector(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, showMetricsSelector: boolean }>,
    ) {
      const { dashboardId, panelId, showMetricsSelector } = action.payload
      ensure(state, dashboardId, panelId).showMetricsSelector = showMetricsSelector
    },

    setSelectedPeriodPerformance(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedPeriodPerformance: string }>,
    ) {
      const { dashboardId, panelId, selectedPeriodPerformance } = action.payload
      ensure(state, dashboardId, panelId).selectedPeriodPerformance = selectedPeriodPerformance
    },

    setSortMetricId(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, sortMetricId: string | null }>,
    ) {
      const { dashboardId, panelId, sortMetricId } = action.payload
      ensure(state, dashboardId, panelId).sortMetricId = sortMetricId
    },

    setSortDirection(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, sortDirection: "ASC" | "DESC" }>,
    ) {
      const { dashboardId, panelId, sortDirection } = action.payload
      ensure(state, dashboardId, panelId).sortDirection = sortDirection
    },

    setSelectedTableLevels(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedTableLevels: string[] }>,
    ) {
      const { dashboardId, panelId, selectedTableLevels } = action.payload
      ensure(state, dashboardId, panelId).selectedTableLevels = selectedTableLevels
    },

    setDatagridState(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, datagridState: Record<string, unknown> }>,
    ) {
      const { dashboardId, panelId, datagridState } = action.payload
      ensure(state, dashboardId, panelId).datagridState = datagridState
    },

    /**
     * Seeds initial slice state from a module's persisted config (initialView,
     * sortMetricId, selectedTableLevels). No-op if the panel already exists in
     * the slice — preferences loaded before the panel mounted take precedence.
     */
    initFromModuleConfig(
      state,
      action: PayloadAction<{
        dashboardId: string
        panelId: string
        view?: string | null
        sortMetricId?: string | null
        selectedTableLevels?: string[]
      }>,
    ) {
      const { dashboardId, panelId, view, sortMetricId, selectedTableLevels } = action.payload
      initInstance(state, dashboardId, panelId, {
        ...(view != null && { view }),
        ...(sortMetricId != null && { sortMetricId }),
        ...(selectedTableLevels != null && { selectedTableLevels }),
      })
    },
  }),
})

export default factorsViewSlice.reducer
